import { Aide } from '@/components/ui/aide';
import { useSyncOuEnLigne } from '../../context/SyncContext';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { apiErrorMessage, clientsAPI } from '../../lib/api';
import { economicsAPI } from '../../lib/economicsApi';
import { fetchAllPages } from '../../lib/fetchAllPages';
import { formatDate, formatMontant, formatNombre, francisaTexte } from '../../lib/formats';
import { libelle, nomLisible, optionsLibelles, referenceLisible } from '../../lib/libelles';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { StatsCompactes } from '../ui/stats-compactes';
import { SearchableSelect } from '../ui/searchable-select';
import { toast } from '../ui/sonner';
import { useRafraichirSur } from '../../hooks/useRafraichirSur';

/**
 * « Fret du trajet » — ce que rapporte le transport de ce trajet, puis ce qui en est
 * encaissé.
 *
 * Étape du parcours: vendre, puis encaisser. Deux modes:
 *  - « Facturé à un client »: le fret devient une facture de transport;
 *  - « Valeur interne de référence »: notre propre carburant, aucune facture; la
 *    valeur sert à mesurer le camion et le bilan l'élimine.
 *
 * Le montant se lit toujours en clair: « 45 000 L × 45 FCFA = 2 025 000 FCFA ». Le
 * tarif au litre est proposé par le barème de fret du corridor / de la destination;
 * l'exploitant peut le corriger ou saisir un montant forfaitaire.
 *
 * Avance reçue et Paiement reçu sont des encaissements du trajet, affichés à titre
 * d'information: ils ne sont jamais comptés comme recette.
 *
 * Visible seulement pour la direction (`canSeeMoney`). Props: { trip, onChanged }.
 */

const BASES = [
  { code: 'CAPACITE', litres: 'capacity' },
  { code: 'CHARGE', litres: 'loaded' },
  { code: 'LIVRE', litres: 'delivered' },
];

const nombre = (valeur) => {
  const n = Number(valeur);
  return Number.isFinite(n) ? n : 0;
};

const litresConnus = (valeur) => valeur !== null && valeur !== undefined && Number.isFinite(Number(valeur));

/** Tarif au litre: entier si rond, sinon deux décimales. */
const formatTarif = (tarif) => formatNombre(tarif, Number.isInteger(Number(tarif)) ? 0 : 2);

/** « 45 000 L × 45 FCFA = 2 025 000 FCFA », ou le montant forfaitaire seul. */
export const phraseDuCalcul = ({ litres, tarif, montant, devise = 'XOF', forfait = false }) => {
  if (forfait) return `Montant forfaitaire: ${formatMontant(montant, devise)}`;
  if (!litresConnus(litres)) return null;
  if (!(nombre(tarif) > 0)) return null;
  const total = nombre(litres) * nombre(tarif);
  return `${formatNombre(litres)} L × ${formatTarif(tarif)} FCFA = ${formatMontant(total, devise)}`;
};

const formulaireDepuis = (vue) => ({
  mode: vue?.mode || 'FACTURE',
  basis: vue?.basis || 'CAPACITE',
  tarif: vue?.rate_per_liter ?? vue?.reference_tariff?.price_per_liter ?? '',
  forfait: vue?.source === 'MONTANT',
  montant: vue?.source === 'MONTANT' && vue?.amount != null ? String(vue.amount) : '',
  clientId: vue?.client?.id || '',
});

const Etape = () => (
  <p className="readout-label">Étape du parcours: vendre, puis encaisser</p>
);

export default function TripFreightPanel({ trip, onChanged }) {
  const { canSeeMoney, hasModule } = useAuth();
  const tripId = trip?.id;
  const autorise =
    Boolean(canSeeMoney) && !(typeof hasModule === 'function' && !hasModule('finance_cockpit'));

  const [vue, setVue] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [indisponible, setIndisponible] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [edition, setEdition] = useState(false);
  const [form, setForm] = useState(formulaireDepuis(null));
  const [reference, setReference] = useState(null);
  const [tarifModifie, setTarifModifie] = useState(false);
  const [clients, setClients] = useState([]);
  const [enCours, setEnCours] = useState(false);
  // L'argent attend le serveur: boutons inactifs sans lui.
  const enLigne = useSyncOuEnLigne().isOnline !== false;
  const [retraitDemande, setRetraitDemande] = useState(false);
  const requete = useRef(0);

  const appliquer = useCallback((reponse) => {
    setVue(reponse);
    setForm(formulaireDepuis(reponse));
    setReference(reponse?.reference_tariff || null);
    setTarifModifie(false);
    setEdition(!reponse?.configured);
    setRetraitDemande(false);
  }, []);

  const charger = useCallback(async () => {
    if (!tripId || !autorise) return;
    const numero = requete.current + 1;
    requete.current = numero;
    setChargement(true);
    setErreur(null);
    try {
      const reponse = await economicsAPI.getTripFreight(tripId);
      if (requete.current !== numero) return;
      appliquer(reponse);
    } catch (error) {
      if (requete.current !== numero) return;
      // Module ou rôle sans accès: le panneau n'a pas lieu d'être, inutile d'alarmer.
      if (error?.response?.status === 403) setIndisponible(true);
      else setErreur(apiErrorMessage(error, 'Impossible de charger le fret du trajet.'));
    } finally {
      if (requete.current === numero) setChargement(false);
    }
  }, [tripId, autorise, appliquer]);

  useEffect(() => {
    charger();
  }, [charger]);
  useRafraichirSur(['trajets', 'finance'], charger, { actif: !edition && !enCours });

  // Les clients ne se chargent que si on en a besoin: formulaire ouvert en mode facturé.
  const besoinClients = autorise && edition && form.mode === 'FACTURE';
  useEffect(() => {
    if (!besoinClients || clients.length > 0) return undefined;
    let annule = false;
    fetchAllPages((params) => clientsAPI.getAllPaged(params))
      .then(({ items }) => {
        if (!annule) setClients(items || []);
      })
      .catch(() => {});
    return () => {
      annule = true;
    };
  }, [besoinClients, clients.length]);

  const optionsClients = useMemo(
    () =>
      clients.map((client) => ({
        value: client.id,
        label: nomLisible(client.name, client.company_name) || 'Client sans nom',
        description: client.city || undefined,
      })),
    [clients],
  );

  if (!autorise || indisponible || !tripId) return null;

  const devise = vue?.currency || 'XOF';
  const litres = vue?.liters || {};
  const baseActive = BASES.find((b) => b.code === form.basis) || BASES[0];
  const litresBase = litres[baseActive.litres];
  const tarifNombre = nombre(form.tarif);
  const phrase = phraseDuCalcul({
    litres: litresBase,
    tarif: tarifNombre,
    montant: nombre(form.montant),
    devise,
    forfait: form.forfait && nombre(form.montant) > 0,
  });

  const changerChamp = (cle) => (event) => {
    const valeur = event.target.value;
    setForm((prev) => ({ ...prev, [cle]: valeur }));
    if (cle === 'tarif') setTarifModifie(true);
  };

  const changerClient = async (identifiant) => {
    setForm((prev) => ({ ...prev, clientId: identifiant }));
    try {
      const propose = await economicsAPI.getFreightTariff({
        corridorId: trip?.corridor_id,
        destination: vue?.destination,
        clientId: identifiant || undefined,
      });
      setReference(propose);
      if (!tarifModifie && propose) setForm((prev) => ({ ...prev, tarif: propose.price_per_liter }));
    } catch (error) {
      // Le tarif proposé est une aide: son absence ne bloque pas la saisie.
    }
  };

  const enregistrer = async (event) => {
    event.preventDefault();
    const corps = {
      mode: form.mode,
      basis: form.basis,
      rate_per_liter: null,
      amount: null,
      client_id: form.mode === 'FACTURE' ? form.clientId || null : null,
    };
    if (form.forfait) {
      if (!(nombre(form.montant) > 0)) {
        toast.error('Indiquez le montant forfaitaire du fret.');
        return;
      }
      corps.amount = nombre(form.montant);
    } else if (tarifNombre > 0) {
      // Un tarif identique à celui du barème reste « du barème »: on n'envoie rien.
      if (!(reference && nombre(reference.price_per_liter) === tarifNombre)) corps.rate_per_liter = tarifNombre;
    } else if (!reference) {
      toast.error('Indiquez le tarif au litre ou un montant forfaitaire: aucun barème ne couvre ce trajet.');
      return;
    }
    setEnCours(true);
    try {
      const reponse = await economicsAPI.saveTripFreight(tripId, corps);
      appliquer(reponse);
      toast.success('Fret du trajet enregistré');
      await onChanged?.();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Impossible d'enregistrer le fret du trajet."));
    } finally {
      setEnCours(false);
    }
  };

  const genererFacture = async () => {
    setEnCours(true);
    try {
      const reponse = await economicsAPI.createFreightInvoice(tripId, form.clientId || vue?.client?.id);
      appliquer(reponse);
      toast.success(
        reponse?.invoice_created === false
          ? 'La facture de transport existait déjà.'
          : 'Facture de transport générée',
      );
      await onChanged?.();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Impossible de générer la facture de transport.'));
    } finally {
      setEnCours(false);
    }
  };

  const retirer = async () => {
    if (!retraitDemande) {
      setRetraitDemande(true);
      return;
    }
    setEnCours(true);
    try {
      const reponse = await economicsAPI.clearTripFreight(tripId);
      appliquer(reponse);
      toast.success('Fret du trajet retiré');
      await onChanged?.();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Impossible de retirer le fret du trajet.'));
    } finally {
      setEnCours(false);
      setRetraitDemande(false);
    }
  };

  const configure = Boolean(vue?.configured);
  const facture = vue?.invoice || null;
  const modeFacture = vue?.mode === 'FACTURE';
  const montantFret = nombre(vue?.amount);
  const phraseEnregistree = configure
    ? phraseDuCalcul({
        litres: litres[(BASES.find((b) => b.code === vue.basis) || BASES[0]).litres],
        tarif: vue.rate_per_liter,
        montant: vue.amount,
        devise,
        forfait: vue.source === 'MONTANT',
      })
    : null;

  return (
    <section className="space-y-4 rounded-xl border border-border bg-card p-4" data-testid="trip-freight-panel">
      <div className="space-y-1">
        <Etape />
        <h3 className="text-base font-semibold">Fret du trajet</h3>
      </div>

      {chargement && !vue && <p className="text-sm text-muted-foreground">Chargement du fret…</p>}

      {erreur && (
        <div className="flex flex-wrap items-center gap-3 text-sm text-destructive" role="alert">
          <span>{erreur}</span>
          <Button type="button" variant="outline" size="sm" onClick={charger}>
            Réessayer
          </Button>
        </div>
      )}

      {vue && !edition && configure && (
        <div className="space-y-4" data-testid="fret-resume">
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">
              {libelle('freight_mode', vue.mode)} · {libelle('freight_basis', vue.basis)} ·{' '}
              {libelle('freight_source', vue.source)}
            </p>
            {phraseEnregistree ? (
              <p className="text-base font-semibold tabular-nums" data-testid="fret-calcul">
                {phraseEnregistree}
              </p>
            ) : (
              <p className="text-base font-semibold tabular-nums">Fret: {formatMontant(vue.amount, devise)}</p>
            )}
            <p className="text-sm text-muted-foreground">{libelle('freight_status', vue.revenue_status)}</p>
          </div>

          {facture && (
            <p className="text-sm" data-testid="fret-facture">
              Facture de transport {referenceLisible(facture.invoice_number, 'sans numéro')} du{' '}
              {formatDate(facture.invoice_date)} · {libelle('invoiceStatus', facture.status)} · total{' '}
              {formatMontant(facture.total_amount, devise)}
            </p>
          )}

          {(modeFacture || nombre(vue.received_total) > 0) && (
            <div data-testid="fret-encaissement">
              <StatsCompactes
                stats={[
                  {
                    label: facture ? 'Montant facturé' : 'Montant du fret',
                    valeur: nombre(vue.due_amount),
                    unite: 'FCFA',
                  },
                  { label: 'Avance reçue', valeur: nombre(vue.advance_received), unite: 'FCFA' },
                  { label: 'Paiement reçu', valeur: nombre(vue.payment_received), unite: 'FCFA' },
                  {
                    label: 'Reste à encaisser',
                    valeur: nombre(vue.remaining_to_collect),
                    unite: 'FCFA',
                    ton: nombre(vue.remaining_to_collect) > 0 ? 'attention' : 'positif',
                  },
                ]}
              />
            </div>
          )}

          {Array.isArray(vue.receipts) && vue.receipts.length > 0 && (
            <ul className="space-y-1 text-sm" data-testid="fret-encaissements">
              {vue.receipts.map((recu, index) => (
                <li key={recu.id || index} className="flex flex-wrap justify-between gap-2 border-b border-border/50 py-1">
                  <span>
                    {formatDate(recu.date)} · {libelle('freight_stage', recu.stage)}
                    {recu.note ? ` · ${francisaTexte(recu.note)}` : ''}
                  </span>
                  <span className="tabular-nums">{formatMontant(recu.amount, devise)}</span>
                </li>
              ))}
            </ul>
          )}
          {nombre(vue.received_total) > 0 && (
            <Aide titre="Avance et paiement">
              Avance et paiement sont déjà encaissés: ils sont donnés à titre d'information et ne comptent pas comme
              recette en plus du fret.
            </Aide>
          )}

          {Array.isArray(vue.warnings) && vue.warnings.length > 0 && (
            <ul className="space-y-1 rounded-md bg-muted/50 p-3 text-sm text-muted-foreground" data-testid="fret-avertissements">
              {vue.warnings.map((avertissement, index) => (
                <li key={index}>{francisaTexte(avertissement)}</li>
              ))}
            </ul>
          )}

          <div className="flex flex-wrap gap-2">
            {modeFacture && !facture && (
              <Button type="button" onClick={genererFacture} disabled={enCours || !enLigne || !(montantFret > 0)}>
                Générer la facture de transport
              </Button>
            )}
            <Button type="button" variant="outline" onClick={() => setEdition(true)} disabled={enCours || !enLigne}>
              Modifier le fret
            </Button>
            <Button type="button" variant="ghost" onClick={retirer} disabled={enCours || !enLigne || Boolean(facture)}>
              {retraitDemande ? 'Confirmer le retrait du fret' : 'Retirer le fret'}
            </Button>
          </div>
          {facture && (
            <p className="text-xs text-muted-foreground">
              Une facture de transport existe: annulez-la d'abord pour retirer le fret.
            </p>
          )}
        </div>
      )}

      {vue && edition && (
        <form onSubmit={enregistrer} className="space-y-3" data-testid="fret-formulaire">
          <div className="grid gap-3 xl:grid-cols-2">
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Mode</legend>
            <div className="grid gap-2 md:grid-cols-2" role="radiogroup" aria-label="Mode du fret">
              {optionsLibelles('freight_mode', ['FACTURE', 'INTERNE']).map((option) => (
                <label
                  key={option.value}
                  className={`flex cursor-pointer items-start gap-2 rounded-md border p-2.5 text-sm ${
                    form.mode === option.value ? 'border-primary bg-primary/5' : 'border-border'
                  }`}
                >
                  <input
                    type="radio"
                    name="fret-mode"
                    className="mt-0.5"
                    checked={form.mode === option.value}
                    onChange={() => setForm((prev) => ({ ...prev, mode: option.value }))}
                  />
                  <span>
                    <span className="font-medium">{option.label}</span>
                    <span className="block text-xs text-muted-foreground">
                      {option.value === 'FACTURE'
                        ? 'Le fret devient une facture de transport pour un client.'
                        : 'Notre propre carburant: aucune facture, la valeur sert à mesurer le camion.'}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Base de calcul</legend>
            <div className="grid gap-2 md:grid-cols-3" role="radiogroup" aria-label="Base de calcul du fret">
              {BASES.map((base) => (
                <label
                  key={base.code}
                  className={`flex cursor-pointer items-start gap-2 rounded-md border p-2.5 text-sm ${
                    form.basis === base.code ? 'border-primary bg-primary/5' : 'border-border'
                  }`}
                >
                  <input
                    type="radio"
                    name="fret-base"
                    className="mt-0.5"
                    checked={form.basis === base.code}
                    onChange={() => setForm((prev) => ({ ...prev, basis: base.code }))}
                  />
                  <span>
                    <span className="font-medium">{libelle('freight_basis', base.code)}</span>
                    <span className="block text-xs text-muted-foreground">
                      {litresConnus(litres[base.litres])
                        ? `${formatNombre(litres[base.litres])} L`
                        : 'Pas encore connue'}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
          </div>

          <div className="grid gap-3 md:grid-cols-3">
          {form.mode === 'FACTURE' && (
            <div className="space-y-1.5">
              <div className="flex items-center gap-1">
                <Label htmlFor="fret-client">Client à facturer</Label>
                <Aide titre="Client à facturer">
                  Facultatif ici: sans choix, le client unique des commandes du trajet est retenu. Il sera exigé pour
                  générer la facture.
                </Aide>
              </div>
              <SearchableSelect
                id="fret-client"
                value={form.clientId}
                onValueChange={changerClient}
                options={optionsClients}
                placeholder="Choisir le client"
                searchPlaceholder="Rechercher un client…"
                emptyMessage="Aucun client trouvé"
              />
            </div>
          )}

            <div className="space-y-1.5">
              <Label htmlFor="fret-tarif">Tarif au litre (FCFA)</Label>
              <Input
                id="fret-tarif"
                type="number"
                min="0"
                step="any"
                inputMode="decimal"
                value={form.tarif}
                onChange={changerChamp('tarif')}
                disabled={form.forfait}
              />
              <p className="text-xs text-muted-foreground" data-testid="fret-tarif-source">
                {reference
                  ? `Barème de fret${reference.rule_name ? ` « ${reference.rule_name} »` : ''}: ${formatTarif(
                      reference.price_per_liter,
                    )} FCFA le litre.`
                  : "Aucun barème de fret ne couvre ce trajet: saisissez le tarif, ou ajoutez-le dans les règles tarifaires."}
              </p>
            </div>
            <div className="space-y-1.5">
              <label className="flex items-center gap-2 text-sm font-medium">
                <input
                  type="checkbox"
                  checked={form.forfait}
                  onChange={(event) => setForm((prev) => ({ ...prev, forfait: event.target.checked }))}
                />
                Saisir un montant forfaitaire à la place
              </label>
              {form.forfait && (
                <>
                  <Label htmlFor="fret-montant">Montant du fret (FCFA)</Label>
                  <Input
                    id="fret-montant"
                    type="number"
                    min="0"
                    step="1"
                    inputMode="decimal"
                    value={form.montant}
                    onChange={changerChamp('montant')}
                  />
                </>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-stretch gap-3">
          <div className="min-w-[16rem] flex-1 rounded-md bg-muted/50 p-3" aria-live="polite">
            <p className="readout-label">Montant du fret</p>
            <p className="mt-1 text-base font-semibold tabular-nums" data-testid="fret-apercu">
              {phrase ||
                (litresConnus(litresBase)
                  ? 'Indiquez le tarif au litre pour calculer le fret.'
                  : `${libelle('freight_basis', form.basis)}: pas encore connue. Choisissez une autre base ou un montant forfaitaire.`)}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" disabled={enCours || !enLigne}>
              {enCours ? 'Enregistrement…' : 'Enregistrer le fret'}
            </Button>
            {configure && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setForm(formulaireDepuis(vue));
                  setReference(vue.reference_tariff || null);
                  setEdition(false);
                }}
              >
                Annuler
              </Button>
            )}
          </div>
          </div>
        </form>
      )}
    </section>
  );
}
